import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { exchangeSession, migrateAnonymous } from "../lib/auth";
import { clearAnonymousTeacherId } from "../lib/teacher";
import { useAuth } from "../contexts/AuthContext";

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Idempotent under React StrictMode.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      setError("Missing session_id in URL.");
      return;
    }

    (async () => {
      try {
        await exchangeSession(sessionId);
        // Move any anonymous-mode activities to this user (idempotent).
        try {
          const res = await migrateAnonymous();
          if (res?.migrated > 0) {
            toast.success(`Brought over ${res.migrated} previous activities to your account.`);
          }
        } catch (_e) {
          // non-fatal
        }
        clearAnonymousTeacherId();
        await refresh();
        // If the teacher came from /pricing and chose a plan, the
        // top-level <ResumeCheckout /> effect will pick up the
        // localStorage stash and bounce them straight into Stripe
        // (ResumeCheckout listens to user state, not the route, so
        // landing anywhere logged-in is fine for that flow).
        // Otherwise, drop them into their library (My Library is the
        // canonical app home — left rail visible, everything one
        // click away). Before Phase 115 we landed on "/" because the
        // marketing page was the only top-level mount; with AppShell
        // wrapping the logged-in routes, "/dashboard" is the right
        // destination.
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate("/dashboard", { replace: true });
        toast.success("Signed in");
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
        setError(String(detail));
        toast.error("Sign-in failed", { description: String(detail) });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      data-testid="auth-callback"
      className="min-h-screen bg-[#f4f5f7] grid place-items-center px-6"
    >
      <div className="ae-card p-8 max-w-md text-center">
        {!error ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-[#4a6984] mx-auto" />
            <p className="mt-4 font-bold text-[#4a6984]">Finishing sign-in…</p>
            <p className="mt-1 text-sm text-[#4a6984]/70">
              One moment while we link your Google account to Atomic Edu.
            </p>
          </>
        ) : (
          <>
            <p className="font-extrabold text-lg text-[#4a6984]">Sign-in didn&apos;t complete</p>
            <p className="mt-2 text-sm text-[#4a6984]/80">{error}</p>
            <button
              data-testid="auth-callback-back"
              onClick={() => navigate("/")}
              className="ae-cta mt-5 inline-flex items-center px-5 py-2 rounded-md text-sm"
            >
              Back home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
